'use client'

import { useCallback } from 'react'
import { useInView } from 'react-intersection-observer'

import {
  PLACE_SEARCH_EMPTY_DATA_TEXT,
  type IPlaceListResponse,
} from '@/entities/place'
import { EmptyData } from '@/features/seoPlaceSearch/ui/EmptyData'
import { PlaceSearchItem } from '@/features/seoPlaceSearch/ui/PlaceSearchItem'
import { CircularProgressIndicator } from '@/shared/design-system/components'
import { DividerHorizontal } from '@/shared/design-system/components/Divider'
import { COMMON_ERROR_DESCRIPTION, COMMON_ERROR_TITLE } from '@/shared/lib'
import { Result } from '@/shared/ui'

interface ISearchPlaceListProps {
  data: IPlaceListResponse | undefined
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  isError: boolean
  handleRetrySearchPlace: () => void
}

const PlaceSearchList = ({
  data,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  isError,
  handleRetrySearchPlace,
}: ISearchPlaceListProps) => {
  const handleInViewChange = useCallback(
    (inView: boolean) => {
      if (isError) return

      const hasMorePage = hasNextPage && !isFetchingNextPage
      if (inView && hasMorePage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, isError]
  )

  const { ref } = useInView({
    threshold: 0.5,
    rootMargin: '50px',
    onChange: handleInViewChange,
    skip: !hasNextPage || isError,
  })

  if (data?.content?.length === 0) {
    return <EmptyData text={PLACE_SEARCH_EMPTY_DATA_TEXT} />
  }

  return (
    <>
      <ul>
        {data?.content?.map((item, index) => (
          <li key={item.serverId}>
            <PlaceSearchItem place={item} />
            <div className='px-5'>
              {index !== data?.content?.length - 1 && (
                <DividerHorizontal
                  h='h-stroke-width-x1'
                  color='bg-stroke-neutral-normal'
                />
              )}
            </div>
          </li>
        ))}

        {isFetchingNextPage && (
          <CircularProgressIndicator size='medium' variant='gray' />
        )}

        {isError && (
          <Result
            title={COMMON_ERROR_TITLE}
            description={COMMON_ERROR_DESCRIPTION}
            handleRetry={handleRetrySearchPlace}
          />
        )}

        {hasNextPage && !isError && <div ref={ref} />}
      </ul>
    </>
  )
}

export default PlaceSearchList
